// Formularios del catálogo (alta, edición y precio): schemas zod y su traducción a los DTOs de type-kit.
import { z } from 'zod';
import { ItemTypeEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ItemTypeEnum';
import { MdmProviderEnum } from '@fiado/type-kit/bin/retailCatalog/enums/MdmProviderEnum';
import { SaleFinancierEnum } from '@fiado/type-kit/bin/retailWizard/enums/SaleFinancierEnum';
import type {
    AccessorySubcategory,
    BackofficeCreateKitRequest,
    BackofficeCreateProductRequest,
    BackofficeCreateServiceRequest,
    BackofficeCreateSkuPriceRequest,
    BackofficeUpdateProductRequest,
} from '@/api/services/retail-catalog/backoffice';
import type { CatalogProduct } from '@/api/services/retail-catalog/retail-catalog.client';
import { optionalHttpsUrlField, skuField as centralSkuField } from '@/lib/formValidators';

export const PRODUCT_CATEGORY_OPTIONS = [
    { value: 'ACCESSORY', label: 'Accesorio' },
    { value: 'MODEM', label: 'Módem' },
    { value: 'SIM', label: 'SIM' },
    { value: 'PHONE', label: 'Teléfono' },
] as const;

export const TRACKING_MODE_OPTIONS = [
    { value: 'QUANTITY', label: 'Cantidad' },
    { value: 'IMEI', label: 'IMEI' },
    { value: 'SERIAL', label: 'Número de serie' },
] as const;

/** Gamas de menor a mayor, como las lee el negocio. */
export const TIER_FORM_OPTIONS = [
    { value: '', label: 'Sin gama' },
    { value: 'LOW', label: 'Baja' },
    { value: 'MID', label: 'Media' },
    { value: 'HIGH', label: 'Alta' },
    { value: 'PREMIUM', label: 'Premium' },
] as const;

const ACCESSORY_SUBCATEGORY_LABEL: Record<AccessorySubcategory, string> = {
    AUDIO: 'Audio',
    CABLES: 'Cables',
    CHARGING: 'Carga',
    OTHER: 'Otros',
    PROTECTION: 'Protección',
    WEARABLES: 'Wearables',
};

/** Subcategorías de un accesorio, en orden alfabético. */
export const ACCESSORY_SUBCATEGORY_OPTIONS = (
    Object.entries(ACCESSORY_SUBCATEGORY_LABEL) as [AccessorySubcategory, string][]
)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));

const SALE_PRIORITY_MAX = 100;

type Financier = Exclude<SaleFinancierEnum, SaleFinancierEnum.NONE>;

const FINANCIER_LABEL: Record<Financier, string> = {
    [SaleFinancierEnum.CREDIYA]: 'CrediYa',
    [SaleFinancierEnum.INNOVA]: 'Innovación',
    [SaleFinancierEnum.LESPAGO]: 'LESPAGO',
    [SaleFinancierEnum.PAYJOY]: 'PayJoy',
    [SaleFinancierEnum.SUREKEEP]: 'SureKeep',
};

/** Financieras que pueden financiar un SKU (`SaleFinancierEnum` sin contado), en orden alfabético. */
export const FINANCIER_OPTIONS: readonly { value: `${Financier}`; label: string }[] = Object.values(
    SaleFinancierEnum,
)
    .filter((value): value is Financier => value !== SaleFinancierEnum.NONE)
    .map((value) => ({ value, label: FINANCIER_LABEL[value] }))
    .sort((a, b) => a.label.localeCompare(b.label));

const MDM_PROVIDER_LABEL: Record<MdmProviderEnum, string> = {
    [MdmProviderEnum.DATACULTR]: 'Datacultr',
    [MdmProviderEnum.MOTOSAFE]: 'Motosafe',
    [MdmProviderEnum.TRUSTONIC]: 'Trustonic',
};

/** Proveedores MDM del catálogo (`MdmProviderEnum`), en orden alfabético. */
export const MDM_PROVIDER_OPTIONS: readonly { value: `${MdmProviderEnum}`; label: string }[] =
    Object.values(MdmProviderEnum)
        .map((value) => ({ value, label: MDM_PROVIDER_LABEL[value] }))
        .sort((a, b) => a.label.localeCompare(b.label));

/** Proveedores que solo bloquean bajo demanda; el backend rechaza el modo programado. */
export const ONDEMAND_ONLY_MDM_PROVIDERS: readonly string[] = [MdmProviderEnum.MOTOSAFE];

export const MDM_LOCK_MODE_OPTIONS = [
    { value: 'ONDEMAND', label: 'Bajo demanda' },
    { value: 'SCHEDULED', label: 'Programado' },
] as const;

type OptionValue<T extends readonly { value: string }[]> = T[number]['value'];

const valuesOf = <T extends readonly { value: string }[]>(options: T) =>
    options.map((o) => o.value) as [OptionValue<T>, ...OptionValue<T>[]];

const skuField = centralSkuField();

const text = z.string();

const DIGITS = /^\d+$/;
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Vacío o un entero entre `min` y `max`. */
const optionalInt = (max: number, message: string) =>
    z.string().refine((value) => {
        const trimmed = value.trim();
        return trimmed === '' || (DIGITS.test(trimmed) && Number(trimmed) <= max);
    }, message);

/** true si `YYYY-MM-DD` existe en el calendario (descarta 30 de febrero). */
function isCalendarDate(value: string): boolean {
    const match = DATE_ONLY.exec(value);
    if (!match) return false;
    const [, year, month, day] = match.map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

/** Renglones con contenido, recortados. */
function linesOf(value: string): string[] {
    return value
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line !== '');
}

type SpecEntry = [key: string, value: string];

/** Cada renglón `clave: valor` a su par; `null` si un renglón no trae clave, dos puntos o valor. */
function specEntriesOf(value: string): SpecEntry[] | null {
    const entries: SpecEntry[] = [];
    for (const line of linesOf(value)) {
        const colon = line.indexOf(':');
        const key = line.slice(0, colon).trim();
        const entryValue = line.slice(colon + 1).trim();
        if (colon < 1 || key === '' || entryValue === '') return null;
        entries.push([key, entryValue]);
    }
    return entries;
}

const commercialFields = {
    salePriority: optionalInt(
        SALE_PRIORITY_MAX,
        `La prioridad es un número entero de 0 a ${String(SALE_PRIORITY_MAX)}.`,
    ),
    warrantyMonths: optionalInt(
        Number.MAX_SAFE_INTEGER,
        'La garantía es un número entero de meses; 0 es sin garantía.',
    ),
    launchDate: z
        .string()
        .refine(
            (value) => value.trim() === '' || isCalendarDate(value.trim()),
            'Escribe una fecha válida.',
        ),
    highlightsText: text,
    specsText: z.string().superRefine((value, ctx) => {
        const entries = specEntriesOf(value);
        if (entries === null) {
            ctx.addIssue({
                code: 'custom',
                message: 'Escribe cada dato en su renglón como «clave: valor».',
            });
            return;
        }
        const keys = entries.map(([key]) => key);
        if (new Set(keys).size !== keys.length) {
            ctx.addIssue({
                code: 'custom',
                message: 'Hay una clave repetida en la ficha técnica.',
            });
        }
    }),
    accessorySubcategory: z.enum(['', ...valuesOf(ACCESSORY_SUBCATEGORY_OPTIONS)]),
};

const baseItemFields = {
    sellable: z.boolean(),
    showInSalesFlow: z.boolean(),
    externalId: text,
};

const productFields = {
    ...baseItemFields,
    category: z.enum(['', ...valuesOf(PRODUCT_CATEGORY_OPTIONS)]),
    tier: z.enum(valuesOf(TIER_FORM_OPTIONS)),
    brand: text,
    model: text,
    capacity: text,
    color: text,
    description: text,
    imageUrl: optionalHttpsUrlField,
    eligibleFinanciers: z.array(z.string()),
    mdmEnabled: z.boolean(),
    mdmProvider: z.enum(['', ...valuesOf(MDM_PROVIDER_OPTIONS)]),
    mdmLockMode: z.enum(['', ...valuesOf(MDM_LOCK_MODE_OPTIONS)]),
    ...commercialFields,
};

const componentSchema = z.object({
    sku: skuField,
    qty: z.number().int('La cantidad debe ser entera.').min(1, 'La cantidad mínima es 1.'),
});

type RefineContext = z.RefinementCtx;

function requireText(ctx: RefineContext, value: string, path: string, message: string) {
    if (value.trim() === '') ctx.addIssue({ code: 'custom', path: [path], message });
}

/** Marca, modelo, categoría y MDM coherente de un producto suelto. */
function refineProduct(
    form: {
        category: string;
        brand: string;
        model: string;
        mdmEnabled: boolean;
        mdmProvider: string;
        mdmLockMode: string;
        accessorySubcategory: string;
    },
    ctx: RefineContext,
) {
    if (form.accessorySubcategory !== '' && form.category !== 'ACCESSORY') {
        ctx.addIssue({
            code: 'custom',
            path: ['accessorySubcategory'],
            message: 'La subcategoría solo aplica a un accesorio.',
        });
    }
    if (form.category === '') {
        ctx.addIssue({ code: 'custom', path: ['category'], message: 'Elige la categoría.' });
    }
    requireText(ctx, form.brand, 'brand', 'Escribe la marca.');
    requireText(ctx, form.model, 'model', 'Escribe el modelo.');
    refineMdm(form, ctx);
}

/** MDM coherente: solo en teléfono, con proveedor y modo de bloqueo. */
function refineMdm(
    form: { category: string; mdmEnabled: boolean; mdmProvider: string; mdmLockMode: string },
    ctx: RefineContext,
) {
    if (!form.mdmEnabled) return;
    if (form.category !== 'PHONE') {
        ctx.addIssue({
            code: 'custom',
            path: ['mdmEnabled'],
            message: 'Solo un teléfono lleva MDM.',
        });
    }
    if (form.mdmProvider === '') {
        ctx.addIssue({ code: 'custom', path: ['mdmProvider'], message: 'Elige el proveedor MDM.' });
    }
    if (form.mdmLockMode === '') {
        ctx.addIssue({
            code: 'custom',
            path: ['mdmLockMode'],
            message: 'Elige el modo de bloqueo.',
        });
    }
    if (
        ONDEMAND_ONLY_MDM_PROVIDERS.includes(form.mdmProvider) &&
        form.mdmLockMode === 'SCHEDULED'
    ) {
        ctx.addIssue({
            code: 'custom',
            path: ['mdmLockMode'],
            message: 'Motosafe solo bloquea bajo demanda.',
        });
    }
}

/** Un componente no se repite dentro del kit. */
function refineComponents(components: { sku: string }[], ctx: RefineContext) {
    const seen = new Set<string>();
    components.forEach((component, index) => {
        const sku = component.sku.trim();
        if (seen.has(sku)) {
            ctx.addIssue({
                code: 'custom',
                path: ['components', index, 'sku'],
                message: 'Este componente ya está en el kit.',
            });
        }
        seen.add(sku);
    });
}

/** Pesos con hasta dos decimales; se toleran comas de miles. */
const PESOS_PATTERN = /^\d+(\.\d{1,2})?$/;

function normalizePesos(value: string): string {
    return value.trim().replace(/,/g, '');
}

/** Pesos escritos por el usuario a centavos, sin pasar por flotantes. */
export function pesosToCents(value: string): number {
    const [whole, fraction = ''] = normalizePesos(value).split('.');
    return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

const pesosField = (required: boolean) =>
    z
        .string()
        .refine(
            (value) =>
                normalizePesos(value) === ''
                    ? !required
                    : PESOS_PATTERN.test(normalizePesos(value)),
            required
                ? 'Escribe el precio en pesos, con hasta dos decimales.'
                : 'Escribe un monto en pesos, con hasta dos decimales.',
        );

/** Precio de contado que se pide en el alta y se registra justo después de crear el SKU. */
const createPriceFields = { basePrice: pesosField(true) };

export const productFormSchema = z
    .object({
        sku: skuField,
        trackingMode: z.enum(['', ...valuesOf(TRACKING_MODE_OPTIONS)]),
        ...productFields,
        ...createPriceFields,
    })
    .superRefine((form, ctx) => {
        if (form.trackingMode === '') {
            ctx.addIssue({
                code: 'custom',
                path: ['trackingMode'],
                message: 'Elige cómo se rastrea.',
            });
        }
        refineProduct(form, ctx);
    });

export type ProductForm = z.infer<typeof productFormSchema>;

export const kitFormSchema = z
    .object({
        sku: skuField,
        ...baseItemFields,
        description: text,
        imageUrl: optionalHttpsUrlField,
        eligibleFinanciers: z.array(z.string()),
        components: z.array(componentSchema).min(1, 'Agrega al menos un componente.'),
        ...createPriceFields,
    })
    .superRefine((form, ctx) => {
        refineComponents(form.components, ctx);
    });

export type KitForm = z.infer<typeof kitFormSchema>;

export const serviceFormSchema = z
    .object({ sku: skuField, ...baseItemFields, description: text, ...createPriceFields })
    .superRefine((form, ctx) => {
        requireText(ctx, form.description, 'description', 'Escribe el nombre del servicio.');
    });

export type ServiceForm = z.infer<typeof serviceFormSchema>;

export const EMPTY_PRODUCT_FORM: ProductForm = {
    sku: '',
    category: '',
    trackingMode: '',
    tier: '',
    brand: '',
    model: '',
    capacity: '',
    color: '',
    description: '',
    imageUrl: '',
    externalId: '',
    sellable: true,
    showInSalesFlow: true,
    eligibleFinanciers: [],
    mdmEnabled: false,
    mdmProvider: '',
    mdmLockMode: '',
    salePriority: '',
    warrantyMonths: '',
    launchDate: '',
    highlightsText: '',
    specsText: '',
    accessorySubcategory: '',
    basePrice: '',
};

export const EMPTY_KIT_FORM: KitForm = {
    sku: '',
    description: '',
    imageUrl: '',
    externalId: '',
    sellable: true,
    showInSalesFlow: true,
    eligibleFinanciers: [],
    components: [],
    basePrice: '',
};

export const EMPTY_SERVICE_FORM: ServiceForm = {
    sku: '',
    description: '',
    externalId: '',
    sellable: true,
    showInSalesFlow: true,
    basePrice: '',
};

/** Solo los textos con contenido, recortados. */
function presentTexts<K extends string>(form: Record<K, string>, keys: readonly K[]) {
    const out: Partial<Record<K, string>> = {};
    for (const key of keys) {
        const value = form[key].trim();
        if (value !== '') out[key] = value;
    }
    return out;
}

type MdmFields = Pick<BackofficeUpdateProductRequest, 'mdmEnabled' | 'mdmProvider' | 'mdmLockMode'>;

function mdmOf(form: Pick<ProductForm, 'mdmEnabled' | 'mdmProvider' | 'mdmLockMode'>): MdmFields {
    if (!form.mdmEnabled) return { mdmEnabled: false, mdmProvider: null, mdmLockMode: null };
    return {
        mdmEnabled: true,
        mdmProvider: form.mdmProvider as MdmFields['mdmProvider'],
        mdmLockMode: form.mdmLockMode as MdmFields['mdmLockMode'],
    };
}

const PRODUCT_TEXTS = [
    'brand',
    'model',
    'capacity',
    'color',
    'description',
    'imageUrl',
    'externalId',
] as const;

type CommercialForm = Pick<
    ProductForm,
    'salePriority' | 'warrantyMonths' | 'launchDate' | 'highlightsText' | 'specsText'
>;

/** Muestra un valor de la ficha técnica; lo que no es texto va como JSON. */
function specDisplayOf(value: unknown): string {
    return typeof value === 'string' ? value : JSON.stringify(value);
}

function specsTextOf(specs: Record<string, unknown> | null | undefined): string {
    return Object.entries(specs ?? {})
        .map(([key, value]) => `${key}: ${specDisplayOf(value)}`)
        .join('\n');
}

/** El mapa de la ficha técnica; un valor que no se tocó conserva su tipo original. */
function specsOf(form: CommercialForm, original: Record<string, unknown> = {}) {
    const specs: Record<string, unknown> = {};
    for (const [key, value] of specEntriesOf(form.specsText) ?? []) {
        const kept = Object.hasOwn(original, key) && specDisplayOf(original[key]) === value;
        specs[key] = kept ? original[key] : value;
    }
    return specs;
}

/** Prioridad, garantía y lanzamiento escritos; lo vacío se omite. */
function commercialScalarsOf(form: CommercialForm) {
    const out: Pick<
        BackofficeUpdateProductRequest,
        'salePriority' | 'warrantyMonths' | 'launchDate'
    > = {};
    if (form.salePriority.trim() !== '') out.salePriority = Number(form.salePriority.trim());
    if (form.warrantyMonths.trim() !== '') out.warrantyMonths = Number(form.warrantyMonths.trim());
    if (form.launchDate.trim() !== '') out.launchDate = form.launchDate.trim();
    return out;
}

function subcategoryOf(form: Pick<ProductForm, 'category' | 'accessorySubcategory'>) {
    return form.category === 'ACCESSORY' && form.accessorySubcategory !== ''
        ? form.accessorySubcategory
        : undefined;
}

/** Campos de la rejilla «Producto» que ve cada categoría: gama y capacidad solo en un teléfono. */
export type ProductGridField =
    | 'category'
    | 'tier'
    | 'accessorySubcategory'
    | 'brand'
    | 'model'
    | 'capacity'
    | 'color';

export function productGridFieldsOf(category: string): ProductGridField[] {
    const isPhone = category === 'PHONE';
    return [
        'category',
        ...(isPhone ? (['tier'] as const) : []),
        ...(category === 'ACCESSORY' ? (['accessorySubcategory'] as const) : []),
        'brand',
        'model',
        ...(isPhone ? (['capacity'] as const) : []),
        'color',
    ];
}

/** Vacía gama y capacidad fuera de un teléfono, para no mandar lo que quedó escrito antes. */
function withoutPhoneOnly<T extends { category: string; tier: string; capacity: string }>(
    form: T,
): T {
    return form.category === 'PHONE' ? form : { ...form, tier: '', capacity: '' };
}

export function toCreateProductRequest(input: ProductForm): BackofficeCreateProductRequest {
    const form = withoutPhoneOnly(input);
    const mdm = mdmOf(form);
    const highlights = linesOf(form.highlightsText);
    const specs = specsOf(form);
    const accessorySubcategory = subcategoryOf(form);
    return {
        sku: form.sku.trim(),
        category: form.category as BackofficeCreateProductRequest['category'],
        trackingMode: form.trackingMode as BackofficeCreateProductRequest['trackingMode'],
        sellable: form.sellable,
        showInSalesFlow: form.showInSalesFlow,
        ...presentTexts(form, PRODUCT_TEXTS),
        ...(form.tier === '' ? {} : { tier: form.tier as BackofficeCreateProductRequest['tier'] }),
        eligibleFinanciers: form.eligibleFinanciers,
        ...(mdm.mdmEnabled ? mdm : { mdmEnabled: false }),
        ...commercialScalarsOf(form),
        ...(highlights.length > 0 ? { highlights } : {}),
        ...(Object.keys(specs).length > 0 ? { specs } : {}),
        ...(accessorySubcategory ? { accessorySubcategory } : {}),
    };
}

function trimmedComponents(components: { sku: string; qty: number }[]) {
    return components.map((c) => ({ sku: c.sku.trim(), qty: c.qty }));
}

export function toCreateKitRequest(form: KitForm): BackofficeCreateKitRequest {
    return {
        sku: form.sku.trim(),
        sellable: form.sellable,
        showInSalesFlow: form.showInSalesFlow,
        ...presentTexts(form, ['description', 'imageUrl', 'externalId'] as const),
        components: trimmedComponents(form.components),
        eligibleFinanciers: form.eligibleFinanciers,
    };
}

export function toCreateServiceRequest(form: ServiceForm): BackofficeCreateServiceRequest {
    return {
        sku: form.sku.trim(),
        description: form.description.trim(),
        sellable: form.sellable,
        showInSalesFlow: form.showInSalesFlow,
        ...presentTexts(form, ['externalId'] as const),
    };
}

/** Qué es el SKU para el alta y la edición: producto suelto, kit o servicio. */
export type CatalogItemKind = 'PRODUCT' | 'KIT' | 'SERVICE';

export function itemTypeOf(
    product: Pick<CatalogProduct, 'category' | 'itemType'>,
): CatalogItemKind {
    if (product.itemType === ItemTypeEnum.SERVICE) return 'SERVICE';
    return product.category === 'BUNDLE' ? 'KIT' : 'PRODUCT';
}

/** Lo editable de cualquier SKU; `itemType`, `trackingMode` y el SKU no se editan. */
export const editFormSchema = z.object({
    ...productFields,
    components: z.array(componentSchema),
});

export type EditForm = z.infer<typeof editFormSchema>;

/** El schema de edición según lo que es el SKU. */
export function editSchemaFor(kind: CatalogItemKind) {
    return editFormSchema.superRefine((form, ctx) => {
        if (kind === 'PRODUCT') refineProduct(form, ctx);
        if (kind === 'SERVICE') {
            requireText(ctx, form.description, 'description', 'Escribe el nombre del servicio.');
        }
        if (kind === 'KIT') {
            if (form.components.length === 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['components'],
                    message: 'Agrega al menos un componente.',
                });
            }
            refineComponents(form.components, ctx);
        }
    });
}

/** Edición de un producto de cadena integrada: solo se validan gama, financieras y MDM. */
export const integratedProductEditSchema = editFormSchema.superRefine(refineMdm);

export function editFormOf(product: CatalogProduct): EditForm {
    const category = product.category ?? '';
    return {
        category: category === 'BUNDLE' ? '' : category,
        tier: product.tier ?? '',
        brand: product.brand ?? '',
        model: product.model ?? '',
        capacity: product.capacity ?? '',
        color: product.color ?? '',
        description: product.description ?? '',
        imageUrl: product.imageUrl ?? '',
        externalId: product.externalId ?? '',
        sellable: product.sellable ?? false,
        showInSalesFlow: product.showInSalesFlow ?? false,
        eligibleFinanciers: [...product.eligibleFinanciers],
        mdmEnabled: product.mdmEnabled,
        mdmProvider: product.mdmProvider ?? '',
        mdmLockMode: product.mdmLockMode ?? '',
        salePriority: product.salePriority === undefined ? '' : String(product.salePriority),
        warrantyMonths: product.warrantyMonths === undefined ? '' : String(product.warrantyMonths),
        launchDate: product.launchDate?.slice(0, 10) ?? '',
        highlightsText: (product.highlights ?? []).join('\n'),
        specsText: specsTextOf(product.specs),
        accessorySubcategory: product.accessorySubcategory ?? '',
        components: (product.components ?? []).map((c) => ({ sku: c.sku, qty: c.qty })),
    };
}

const EDITABLE_TEXTS: Record<CatalogItemKind, readonly (typeof PRODUCT_TEXTS)[number][]> = {
    PRODUCT: PRODUCT_TEXTS,
    KIT: ['description', 'imageUrl', 'externalId'],
    SERVICE: ['description', 'externalId'],
};

const sameSet = (a: readonly string[], b: readonly string[]) =>
    a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

const componentsKey = (components: { sku: string; qty: number }[]) =>
    trimmedComponents(components)
        .map((c) => `${c.sku}:${String(c.qty)}`)
        .sort()
        .join('|');

/** El PATCH con solo lo que cambió. Vaciar un texto o la gama no se manda: el DTO no admite null. */
export function toUpdateProductRequest(
    input: EditForm,
    current: CatalogProduct,
): BackofficeUpdateProductRequest {
    const form = withoutPhoneOnly(input);
    const kind = itemTypeOf(current);
    const before = editFormOf(current);
    const patch: BackofficeUpdateProductRequest = {};
    if (form.sellable !== before.sellable) patch.sellable = form.sellable;
    if (form.showInSalesFlow !== before.showInSalesFlow) {
        patch.showInSalesFlow = form.showInSalesFlow;
    }
    for (const key of EDITABLE_TEXTS[kind]) {
        const value = form[key].trim();
        if (value !== '' && value !== before[key].trim()) patch[key] = value;
    }
    if (kind !== 'SERVICE' && !sameSet(form.eligibleFinanciers, before.eligibleFinanciers)) {
        patch.eligibleFinanciers = form.eligibleFinanciers;
    }
    if (kind === 'KIT' && componentsKey(form.components) !== componentsKey(before.components)) {
        patch.components = trimmedComponents(form.components);
    }
    if (kind !== 'PRODUCT') return patch;
    if (form.category !== '' && form.category !== before.category) {
        patch.category = form.category as BackofficeUpdateProductRequest['category'];
    }
    if (form.tier !== '' && form.tier !== before.tier) {
        patch.tier = form.tier as BackofficeUpdateProductRequest['tier'];
    }
    const mdmChanged =
        form.mdmEnabled !== before.mdmEnabled ||
        (form.mdmEnabled &&
            (form.mdmProvider !== before.mdmProvider || form.mdmLockMode !== before.mdmLockMode));
    if (mdmChanged) Object.assign(patch, mdmOf(form));
    return Object.assign(patch, commercialPatchOf(form, before, current));
}

const sameList = (a: readonly string[], b: readonly string[]) => a.join('\n') === b.join('\n');

/** Ficha comercial y subcategoría que cambiaron; vaciar destacados o ficha técnica los borra. */
function commercialPatchOf(
    form: EditForm,
    before: EditForm,
    current: CatalogProduct,
): BackofficeUpdateProductRequest {
    const patch: BackofficeUpdateProductRequest = {};
    const scalars = commercialScalarsOf(form);
    const previous = commercialScalarsOf(before);
    for (const key of ['salePriority', 'warrantyMonths', 'launchDate'] as const) {
        if (scalars[key] !== undefined && scalars[key] !== previous[key]) {
            Object.assign(patch, { [key]: scalars[key] });
        }
    }
    const highlights = linesOf(form.highlightsText);
    if (!sameList(highlights, linesOf(before.highlightsText))) patch.highlights = highlights;
    const specs = JSON.stringify(specEntriesOf(form.specsText) ?? []);
    if (specs !== JSON.stringify(specEntriesOf(before.specsText) ?? [])) {
        patch.specs = specsOf(form, current.specs ?? {});
    }
    const subcategory = subcategoryOf(form);
    if (subcategory !== undefined && subcategory !== before.accessorySubcategory) {
        patch.accessorySubcategory = subcategory;
    }
    return patch;
}

export const priceFormSchema = z.object({
    basePrice: pesosField(true),
});

export type PriceForm = z.infer<typeof priceFormSchema>;

export const EMPTY_PRICE_FORM: PriceForm = { basePrice: '' };

/** Formularios de alta vacíos y sin arreglos compartidos: cada alta arranca de cero. */
export function emptyCreateForm(kind: 'PRODUCT'): ProductForm;
export function emptyCreateForm(kind: 'KIT'): KitForm;
export function emptyCreateForm(kind: 'SERVICE'): ServiceForm;
export function emptyCreateForm(kind: CatalogItemKind): ProductForm | KitForm | ServiceForm {
    if (kind === 'KIT') return structuredClone(EMPTY_KIT_FORM);
    if (kind === 'SERVICE') return structuredClone(EMPTY_SERVICE_FORM);
    return structuredClone(EMPTY_PRODUCT_FORM);
}

/** El precio de contado capturado en el alta, como registro de precio del SKU nuevo. */
export function toInitialPriceRequest(basePrice: string): BackofficeCreateSkuPriceRequest {
    return { basePriceCents: pesosToCents(basePrice) };
}

export function toCreateSkuPriceRequest(form: PriceForm): BackofficeCreateSkuPriceRequest {
    return toInitialPriceRequest(form.basePrice);
}
